import { useTranslation } from '@/i18n';
import { useMemo } from 'react';

export type DuplicateMenuItem = {
  key: string;
  label: string;
  disabled: boolean;
  onClick: () => void;
};

export type UseDuplicateMenuItemsOptions = {
  isCopyable: boolean;
  isProcessing: boolean;
  /**
   * Whether this report can be shared with viewers at all (mirrors the
   * detail drawer's `share_action.visible`). When false, duplication has no
   * "share the copy too" choice, so callers should fall back to a single
   * plain duplicate action instead of this group.
   */
  canShare: boolean;
  /**
   * Whether the report currently has at least one viewer to carry over.
   * Without any viewers there's nothing for "同じ閲覧者へ共有して複製" to
   * offer, so callers should fall back to the plain duplicate action rather
   * than show the group with that choice permanently disabled.
   */
  hasViewers: boolean;
  onDuplicateWithSameViewers: () => void;
  onDuplicateWithoutSharing: () => void;
};

/**
 * Builds the "同じ閲覧者へ共有して複製" / "共有せずに複製" pair shown once a
 * report can be shared with viewers, shared between the list row's action
 * menu and the detail drawer's header duplicate button so both stay in sync.
 */
export const useDuplicateMenuItems = ({
  isCopyable,
  isProcessing,
  canShare,
  hasViewers,
  onDuplicateWithSameViewers,
  onDuplicateWithoutSharing,
}: UseDuplicateMenuItemsOptions): DuplicateMenuItem[] | null => {
  const { t } = useTranslation();

  return useMemo(() => {
    if (!canShare || !hasViewers) return null;
    return [
      {
        key: 'duplicate_with_same_viewers',
        label: t('invoice_reports.action_menu.duplicate_with_same_viewers'),
        disabled: !isCopyable || isProcessing,
        onClick: onDuplicateWithSameViewers,
      },
      {
        key: 'duplicate_without_sharing',
        label: t('invoice_reports.action_menu.duplicate_without_sharing'),
        disabled: !isCopyable || isProcessing,
        onClick: onDuplicateWithoutSharing,
      },
    ];
  }, [
    canShare,
    hasViewers,
    isCopyable,
    isProcessing,
    onDuplicateWithSameViewers,
    onDuplicateWithoutSharing,
    t,
  ]);
};
